import {gql} from '@apollo/client';
import {useEffect} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {isNodeTesting} from 'lib/Browser';
import {ChatEventNode} from 'llm/data-capture/DataCaptureTypes';

import {
  DataCaptureChatSessionQuery,
  DataCaptureChatSessionQueryVariables,
} from './__generated/DataCaptureChatSessionQuery';

export const CHAT_SESSION_QUERY = gql`
  query DataCaptureChatSessionQuery($chatSessionId: StrictString!) @engComponent(name: LLM) @skipAudit {
    chatSession(id: $chatSessionId) {
      ... on ChatSessionNode {
        id
        threads {
          id
          events {
            ... on DisplayMessageChatEventNode {
              id
              createdAt
              role
              messageBlocks {
                ... on DisplayTextBlockNode {
                  text
                }
                ... on DisplayFileBlockNode {
                  externalFileId
                  filename
                  mediaType
                }
              }
            }
          }
        }
      }
    }
  }
`;

export interface UseDataCaptureChatSessionEventsResult {
  events: Array<ChatEventNode>;
  isLoading: boolean;
  errorMessage: string | null;
}

/**
 * Hook to get the most recent chat messages/events for display purposes.
 *
 * Right now, this polls for events every second from the GraphQL endpoint, but the
 * intention is to use websockets in the future.
 */
export function useDataCaptureChatSessionEvents(
  chatSessionId: string | null
): UseDataCaptureChatSessionEventsResult {
  const queryResult = useSkippableBenchlingQuery<
    DataCaptureChatSessionQuery,
    DataCaptureChatSessionQueryVariables
  >(CHAT_SESSION_QUERY, {
    skip: !chatSessionId,
    variables: chatSessionId ? {chatSessionId} : undefined,
    renderCachedDataWhenRefetching: true,
    // For now, poll every 1 second. Disable polling in unit tests since it can cause test flakiness.
    pollInterval: isNodeTesting ? 0 : 1000,
  });

  const hadError = queryResult.type === QueryResultType.ERROR;
  const isChatInaccessible =
    queryResult.type === QueryResultType.SUCCESS && isInaccessible(queryResult.data.chatSession);

  useEffect(() => {
    // When showing an error message to the user, stop polling. This avoids an ugly flicker
    // where the UI briefly switches back to the success case on poll due to the behavior
    // of renderCachedDataWhenRefetching.
    // We may want to still refine this further, e.g. avoiding losing the shown chat on network
    // disconnect
    if (hadError || isChatInaccessible) {
      queryResult.stopPolling();
    }
  }, [hadError, isChatInaccessible, queryResult]);

  if (hadError) {
    return {
      events: [],
      isLoading: false,
      errorMessage: 'An error occurred loading this chat. Please try again later.',
    };
  } else if (queryResult.type === QueryResultType.SKIPPED) {
    return {events: [], isLoading: false, errorMessage: null};
  } else if (queryResult.type === QueryResultType.PENDING) {
    return {events: [], isLoading: true, errorMessage: null};
  }
  const {chatSession} = queryResult.data;
  if (isInaccessible(chatSession)) {
    return {events: [], isLoading: false, errorMessage: 'You do not have permission to access this chat.'};
  }
  const rootThread = chatSession.threads[0];
  return {events: rootThread.events, isLoading: false, errorMessage: null};
}
